import { Cell, Badge, Button, IconButton } from '@telegram-apps/telegram-ui';
import { Villa } from '../types';
import { STATUS_LABELS, STATUS_COLORS } from '../types';
import { createWhatsAppLink, extractDomain } from '../api';
import './VillaCard.css';

const Icon24MoreVertical = () => (
  <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="5" r="1.5"/>
    <circle cx="12" cy="12" r="1.5"/>
    <circle cx="12" cy="19" r="1.5"/>
  </svg>
);

interface VillaCardProps {
  villa: Villa;
  viewMode: 'list' | 'tiles';
  onStatusClick: (villa: Villa) => void;
  onEdit: (villa: Villa) => void;
  isArchived?: boolean;
}

export default function VillaCard({
  villa,
  viewMode,
  onStatusClick,
  onEdit,
  isArchived = false,
}: VillaCardProps) {
  const handleWhatsAppClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (villa.phone) {
      window.open(createWhatsAppLink(villa.phone), '_blank');
    }
  };

  const handleUrlClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(villa.url, '_blank');
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStatusClick(villa);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(villa);
  };

  const handleCardClick = () => {
    if (!isArchived) {
      onEdit(villa);
    }
  };

  // List mode - use Cell component
  if (viewMode === 'list') {
    return (
      <Cell
        className="villa-card"
        onClick={handleCardClick}
        before={villa.image && (
          <div className="villa-card__image-small" onClick={handleUrlClick}>
            <img src={villa.image} alt="" loading="lazy" />
          </div>
        )}
        after={
          <div className="villa-card__after">
            <IconButton
              mode="bezeless"
              onClick={handleEditClick}
            >
              <Icon24MoreVertical />
            </IconButton>
          </div>
        }
        subtitle={
          <div className="villa-card__content">
            <div className="villa-card__meta">
              <button className="villa-card__domain" onClick={handleUrlClick}>
                {extractDomain(villa.url)}
              </button>
              <span className="villa-card__date">{villa.addedAtFormatted}</span>
            </div>

            {villa.priceFormatted && (
              <div className="villa-card__price">{villa.priceFormatted}</div>
            )}

            <div className="villa-card__status-row">
              <Badge
                type="number"
                onClick={handleStatusClick}
                style={{ backgroundColor: STATUS_COLORS[villa.status] }}
              >
                {STATUS_LABELS[villa.status]}
              </Badge>

              {villa.phone ? (
                <Button
                  size="s"
                  mode="outline"
                  onClick={handleWhatsAppClick}
                >
                  WhatsApp
                </Button>
              ) : (
                <Button
                  size="s"
                  mode="gray"
                  onClick={handleEditClick}
                >
                  + Телефон
                </Button>
              )}
            </div>

            {villa.comment && (
              <div className="villa-card__comment">{villa.comment}</div>
            )}
          </div>
        }
        multiline
      >
        {villa.title || extractDomain(villa.url)}
      </Cell>
    );
  }

  // Tiles mode - use custom card with Cell styling
  return (
    <div className="villa-card-tile" onClick={handleCardClick}>
      {villa.image && (
        <div className="villa-card-tile__image" onClick={handleUrlClick}>
          <img src={villa.image} alt="" loading="lazy" />
        </div>
      )}

      <div className="villa-card-tile__content">
        <div className="villa-card-tile__header">
          <div className="villa-card-tile__title">
            {villa.title || extractDomain(villa.url)}
          </div>
          <IconButton mode="bezeless" onClick={handleEditClick}>
            <Icon24MoreVertical />
          </IconButton>
        </div>

        <div className="villa-card-tile__meta">
          <button className="villa-card__domain" onClick={handleUrlClick}>
            {extractDomain(villa.url)}
          </button>
          <span className="villa-card__date">{villa.addedAtFormatted}</span>
        </div>

        {villa.priceFormatted && (
          <div className="villa-card__price">{villa.priceFormatted}</div>
        )}

        <div className="villa-card-tile__status-row">
          <Badge
            type="number"
            onClick={handleStatusClick}
            style={{ backgroundColor: STATUS_COLORS[villa.status] }}
          >
            {STATUS_LABELS[villa.status]}
          </Badge>

          {villa.phone ? (
            <Button
              size="s"
              mode="outline"
              onClick={handleWhatsAppClick}
            >
              WhatsApp
            </Button>
          ) : (
            <Button
              size="s"
              mode="gray"
              onClick={handleEditClick}
            >
              + Телефон
            </Button>
          )}
        </div>

        {villa.comment && (
          <div className="villa-card__comment">{villa.comment}</div>
        )}
      </div>
    </div>
  );
}
